import { formatDate, MagicConfigMixin } from '@gig/magic-common';
import { __extends, __decorate, __metadata } from 'tslib';
import { Prop, Emit, Component, Mixins } from 'vue-property-decorator';
import { AmlStatus } from '@gig/magic-sdk/dist/adapter/users/getAmlStatus';

var _a;
var UploadStatus;
(function (UploadStatus) {
    UploadStatus["NORMAL"] = "normal";
    UploadStatus["FAILED"] = "failed";
    UploadStatus["PENDING"] = "pending";
    UploadStatus["VERIFIED"] = "verified";
    UploadStatus["REJECTED"] = "rejected";
    UploadStatus["VERIFIED_PARTIAL"] = "verifiedpartial";
    UploadStatus["EXPIRED"] = "expired";
    UploadStatus["REQUESTED"] = "requested";
})(UploadStatus || (UploadStatus = {}));
var amlToUploadStatus = (_a = {},
    _a[AmlStatus.NOT_SET] = UploadStatus.NORMAL,
    _a[AmlStatus.PENDING] = UploadStatus.NORMAL,
    _a[AmlStatus.PROCESSING] = UploadStatus.PENDING,
    _a[AmlStatus.REQUESTED] = UploadStatus.REQUESTED,
    _a[AmlStatus.USER_REQUESTED] = UploadStatus.NORMAL,
    _a[AmlStatus.REJECTED] = UploadStatus.REJECTED,
    _a[AmlStatus.APPROVED] = UploadStatus.VERIFIED,
    _a[AmlStatus.PENDING_REQUEST] = UploadStatus.NORMAL,
    _a[AmlStatus.VERIFIED_PARTIAL] = UploadStatus.VERIFIED_PARTIAL,
    _a[AmlStatus.EXPIRED] = UploadStatus.EXPIRED,
    _a);
var excludedDocumentStatuses = [
    AmlStatus.REQUESTED,
    AmlStatus.USER_REQUESTED,
    AmlStatus.EXPIRED,
];
/**
 * Component that allows the user to upload files both on mobile and dekstop
 * devices. Additionally it allows drag-and-drop functionality on desktop.
 */
var FileUploadComponent = /** @class */ (function (_super) {
    __extends(FileUploadComponent, _super);
    function FileUploadComponent() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.files = [];
        _this.supportedFileTypes = [
            'image/bmp',
            'image/png',
            'image/gif',
            'image/jpeg',
            'application/pdf',
        ];
        _this.uploadFormValue = null;
        _this.uploadDisabled = false;
        return _this;
    }
    Object.defineProperty(FileUploadComponent.prototype, "computedButtonText", {
        get: function () {
            return this.buttonText || this.$t('file-upload.button');
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(FileUploadComponent.prototype, "uploadedFiles", {
        get: function () {
            return this.previousFiles.filter(function (file) { return !excludedDocumentStatuses.includes(file.statusText); });
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(FileUploadComponent.prototype, "hasPendingUpload", {
        get: function () {
            return this.files.some(function (_a) {
                var progress = _a.progress;
                return progress < 100;
            });
        },
        enumerable: false,
        configurable: true
    });
    FileUploadComponent.prototype.fileChosen = function () {
        var fileInput = this.$refs.fileInput;
        for (var _i = 0, _a = Array.from(fileInput.files); _i < _a.length; _i++) {
            var file = _a[_i];
            this.addFile(file);
        }
    };
    FileUploadComponent.prototype.fileDropped = function (event) {
        if (!event.dataTransfer) {
            return;
        }
        if (event.dataTransfer.items) {
            var eventFiles = Array.from(event.dataTransfer.items);
            for (var _i = 0, eventFiles_1 = eventFiles; _i < eventFiles_1.length; _i++) {
                var file = eventFiles_1[_i];
                if (file.kind === 'file') {
                    this.addFile(file.getAsFile());
                }
            }
        }
        else {
            var eventFiles = Array.from(event.dataTransfer.files);
            for (var _a = 0, eventFiles_2 = eventFiles; _a < eventFiles_2.length; _a++) {
                var file = eventFiles_2[_a];
                this.addFile(file);
            }
        }
    };
    /**
     * Adds a new file that was selected either with the button or with
     * drag&drop. Emits a `selected` event with an event listener for progress.
     */
    FileUploadComponent.prototype.addFile = function (file) {
        if (this.uploadDisabled) {
            return;
        }
        if (!this.supportedFileTypes.includes(file.type)) {
            this.uploadError(this.$t('file-upload.unsupported-type', {
                file: file.name,
            }));
            return;
        }
        var fileStatus = {
            file: file,
            progress: 0,
            hasError: false,
        };
        this.files.push(fileStatus);
        return {
            file: file,
            uploadFormValue: this.uploadFormValue,
            onProgressChange: function (progress, hasError) {
                if (hasError) {
                    fileStatus.hasError = true;
                    return;
                }
                fileStatus.progress = Math.round(progress * 100);
            },
        };
    };
    FileUploadComponent.prototype.onUploadFormUpdate = function (event) {
        var _a = event.detail[0], value = _a.value, disabled = _a.disabled;
        this.uploadFormValue = value;
        this.uploadDisabled = disabled;
    };
    FileUploadComponent.prototype.uploadError = function (message) {
        return message;
    };
    FileUploadComponent.prototype.isNormal = function (status) {
        return amlToUploadStatus[status] === UploadStatus.NORMAL;
    };
    FileUploadComponent.prototype.isFailed = function (status) {
        return amlToUploadStatus[status] === UploadStatus.FAILED;
    };
    FileUploadComponent.prototype.isPending = function (status) {
        return amlToUploadStatus[status] === UploadStatus.PENDING;
    };
    FileUploadComponent.prototype.isVerified = function (status) {
        return amlToUploadStatus[status] === UploadStatus.VERIFIED;
    };
    FileUploadComponent.prototype.isVerifiedPartial = function (status) {
        return amlToUploadStatus[status] === UploadStatus.VERIFIED_PARTIAL;
    };
    FileUploadComponent.prototype.isRejected = function (status) {
        return amlToUploadStatus[status] === UploadStatus.REJECTED;
    };
    FileUploadComponent.prototype.getStatusTranslation = function (status) {
        if (this.pendingTextVariantId &&
            amlToUploadStatus[status] === UploadStatus.PENDING) {
            return 'file-upload.scan.pending';
        }
        if (this.pendingTextVariantAddress &&
            amlToUploadStatus[status] === UploadStatus.PENDING) {
            return 'file-upload.scan.address.pending';
        }
        switch (amlToUploadStatus[status]) {
            case UploadStatus.FAILED:
                return 'file-upload.failed';
            case UploadStatus.REJECTED:
                return 'file-upload.error';
            case UploadStatus.VERIFIED:
                return 'file-upload.verified';
            case UploadStatus.VERIFIED_PARTIAL:
                return 'file.upload.verified-partial';
            default:
                return 'file-upload.pending';
        }
    };
    FileUploadComponent.prototype.getStatusIcon = function (status) {
        switch (amlToUploadStatus[status]) {
            case UploadStatus.FAILED:
            case UploadStatus.REJECTED:
                return 'file-upload';
            case UploadStatus.PENDING:
            case UploadStatus.VERIFIED_PARTIAL:
                return 'timestamp';
            case UploadStatus.VERIFIED:
                return 'checked';
            default:
                return '';
        }
    };
    FileUploadComponent.prototype.getStatusIconClass = function (status) {
        switch (amlToUploadStatus[status]) {
            case UploadStatus.FAILED:
            case UploadStatus.REJECTED:
                return 'icon--file-rejected';
            case UploadStatus.NORMAL:
            case UploadStatus.PENDING:
            case UploadStatus.VERIFIED_PARTIAL:
                return 'icon--file-pending';
            case UploadStatus.VERIFIED:
                return 'icon--file-verified';
            default:
                return '';
        }
    };
    FileUploadComponent.prototype.handleButtonClick = function (event) {
        if (this.uploadDisabled) {
            event.preventDefault();
            event.stopImmediatePropagation();
            return;
        }
        var fileInput = this.$refs.fileInput;
        if (fileInput) {
            fileInput.click();
        }
    };
    __decorate([
        Prop(String),
        __metadata("design:type", String)
    ], FileUploadComponent.prototype, "uploadFormTag", void 0);
    __decorate([
        Prop(String),
        __metadata("design:type", String)
    ], FileUploadComponent.prototype, "header", void 0);
    __decorate([
        Prop(String),
        __metadata("design:type", String)
    ], FileUploadComponent.prototype, "hint", void 0);
    __decorate([
        Prop({
            type: String,
            default: AmlStatus.NOT_SET,
        }),
        __metadata("design:type", String)
    ], FileUploadComponent.prototype, "status", void 0);
    __decorate([
        Prop(String),
        __metadata("design:type", String)
    ], FileUploadComponent.prototype, "buttonText", void 0);
    __decorate([
        Prop(Boolean),
        __metadata("design:type", Boolean)
    ], FileUploadComponent.prototype, "isHollowVariant", void 0);
    __decorate([
        Prop(Boolean),
        __metadata("design:type", Boolean)
    ], FileUploadComponent.prototype, "pendingTextVariantId", void 0);
    __decorate([
        Prop(Boolean),
        __metadata("design:type", Boolean)
    ], FileUploadComponent.prototype, "pendingTextVariantAddress", void 0);
    __decorate([
        Prop({ type: Boolean, default: false }),
        __metadata("design:type", Boolean)
    ], FileUploadComponent.prototype, "disabled", void 0);
    __decorate([
        Prop({
            type: Array,
            default: function () { return []; },
        }),
        __metadata("design:type", Array)
    ], FileUploadComponent.prototype, "previousFiles", void 0);
    __decorate([
        Prop(Boolean),
        __metadata("design:type", Boolean)
    ], FileUploadComponent.prototype, "alwaysAllowDocumentUpload", void 0);
    __decorate([
        Prop({ type: Boolean, default: false }),
        __metadata("design:type", Boolean)
    ], FileUploadComponent.prototype, "hasRequestedFiles", void 0);
    __decorate([
        Prop(Boolean),
        __metadata("design:type", Boolean)
    ], FileUploadComponent.prototype, "disableManualUpload", void 0);
    __decorate([
        Prop(Boolean),
        __metadata("design:type", Boolean)
    ], FileUploadComponent.prototype, "allowAllDocumentsUpload", void 0);
    __decorate([
        Emit('selected'),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [File]),
        __metadata("design:returntype", void 0)
    ], FileUploadComponent.prototype, "addFile", null);
    __decorate([
        Emit('error'),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [String]),
        __metadata("design:returntype", void 0)
    ], FileUploadComponent.prototype, "uploadError", null);
    FileUploadComponent = __decorate([
        Component({
            name: 'ui-file-upload',
            filters: {
                formatDate: formatDate,
            },
        })
    ], FileUploadComponent);
    return FileUploadComponent;
}(Mixins(MagicConfigMixin)));

export default FileUploadComponent;
export { UploadStatus };
